<script>
import { ref } from 'vue';

// One Copilot thread for the whole tab, shared by the global panel and the one in the conversation's Ask
// tab, so switching tabs, closing the panel or leaving the conversation keeps the thread, as the single
// global panel always did.
const selectedCopilotThreadId = ref(null);
</script>

<script setup>
import { computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import {
  isConversationPanelMounted,
  useOverlayEscape,
  ASK_OPENER_SELECTOR,
} from 'dashboard/composables/useConversationPanel';

const props = defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
  // In the conversation's Ask tab: only the Copilot (the Inspector is the frame, header and close
  // included). Without it, the global panel of the other pages, unchanged.
  embedded: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { uiSettings, updateUISettings, isCopilotPanelVisible } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('polarisAssistants/getRecords');
const uiFlags = useMapGetter('polarisAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.OVERLAY_PANEL_BREAKPOINT
);

const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedAssistantId = ref(null);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_polaris_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for Copilot.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_polaris_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  // Fork Zerc: Polaris roda em instalacao "community" (isEnterprise = false), com a
  // feature destravada por flag. O gate correto e a flag do Polaris, nao isEnterprise.
  const isPolarisEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.POLARIS
  );
  return (
    isPolarisEnabled &&
    isCopilotPanelVisible.value &&
    !uiFlags.value.fetchingList &&
    // Inside a conversation the Copilot is the panel's Ask tab.
    !isConversationPanelMounted.value
  );
});

// Below xl the global panel is an overlay: Escape closes it as ✕ and a click outside do, and the focus goes back
// to the control that opened it (the top bar's Ask Polaris). The click outside ignores that control, whose own
// click closes it (otherwise the click outside closes it and the same click opens it again).
const panelRef = ref(null);
useOverlayEscape({
  isOverlayOpen: computed(
    () => !props.embedded && isSmallScreen.value && shouldShowCopilotPanel.value
  ),
  panel: panelRef,
  close: closeCopilotPanel,
  fallbackOpener: ASK_OPENER_SELECTOR,
});

const handleReset = () => {
  selectedCopilotThreadId.value = null;
};

const sendMessage = async message => {
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
    }
  } catch (error) {
    useAlert(error.message);
  }
};

onMounted(() => {
  // The global instance (always mounted in Dashboard.vue) loads the assistants once; the Ask tab mounts
  // again on every visit and refetching would blank it while `fetchingList` is on.
  if (!props.embedded) store.dispatch('polarisAssistants/get');
});
</script>

<template>
  <Copilot
    v-if="embedded && !uiFlags.fetchingList"
    hide-header
    class="min-h-0 flex-1"
    :messages="messages"
    :support-agent="currentUser"
    :conversation-inbox-type="conversationInboxType"
    :assistants="assistants"
    :active-assistant="activeAssistant"
    @set-assistant="setAssistant"
    @send-message="sendMessage"
    @reset="handleReset"
  />
  <div
    v-else-if="!embedded && shouldShowCopilotPanel"
    ref="panelRef"
    v-on-click-outside="[
      () => closeCopilotPanel(),
      { ignore: [ASK_OPENER_SELECTOR] },
    ]"
    class="bg-n-surface-2 overflow-hidden flex-col fixed bottom-0 top-14 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:w-[320px] md:min-w-[320px] xl:static xl:h-full ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg xl:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
    />
  </div>
  <template v-else />
</template>
